import {
  PhoneOutline24Icon,
  QuestionOutline24Icon,
  ShareOutline24Icon,
} from "@vygruppen/spor-icon-react";
import { useApiAction } from "api/drops/useApiAction";
import { sessionCommandObject } from "api/puzzel/puzzel";
import { CallPauseIconStyled } from "features/Header/PuzzelTelephone/PuzzelStyle";
import { useSetTransfered } from "features/Header/PuzzelTelephone/components/CallControl/views/CallInSession/CallTransfer/TransferProvider";
import { SessionCommands } from "features/Header/PuzzelTelephone/components/types";
import { usePuzzelExtendedStateInformation } from "features/Header/PuzzelTelephone/provider/contexts/ListenForCallStatusContext";
import { useUserValidationState } from "features/Header/PuzzelTelephone/provider/contexts/UserValidationContext";
import { LogLevel, log } from "logging/datadogBrowserLogs";
import { ReactElement, useEffect } from "react";
import { DropsButton } from "shared/components/Spor";

type Props = {
  command: SessionCommands;
  requestId: string;
  destinationPhoneNumber?: string;
  disabled?: boolean;
  destinationAgentId?: string;
  small?: boolean;
};

type ButtonIconText = {
  icon: ReactElement;
  text: string;
};

const getButtonIconText = (
  command: SessionCommands,
  small?: boolean,
): ButtonIconText => {
  switch (command) {
    case SessionCommands.ConsultOff:
    case SessionCommands.Hangup:
      return {
        icon: <PhoneOutline24Icon style={{ rotate: "135deg" }} />,
        text: "Legg på",
      };
    case SessionCommands.ConsultWithAgent:
    case SessionCommands.ConsultWithPhone:
      return {
        icon: <QuestionOutline24Icon />,
        text: small ? "" : "Spørreanrop",
      };
    case SessionCommands.TransferToAgent:
    case SessionCommands.TransferToPhone:
      return {
        icon: <ShareOutline24Icon />,
        text: small ? "" : "Sett videre",
      };
    case SessionCommands.MuteOn:
      return {
        icon: <CallPauseIconStyled />,
        text: "Sett på vent",
      };
    case SessionCommands.MuteOff:
      return {
        icon: <CallPauseIconStyled />,
        text: "Hent inn samtale",
      };

    case SessionCommands.ClientConnected:
    case SessionCommands.ConferenceAdd:
    case SessionCommands.ConferenceRemove:
    case SessionCommands.TransferToConsultant:
    case SessionCommands.TransferToPersonalQueue:
    case SessionCommands.TransferToQueue:
    case SessionCommands.TransferToService:
      return {
        icon: <PhoneOutline24Icon />,
        text: "",
      };
  }
};

const SessionCommand = (props: Props) => {
  const {
    command,
    requestId,
    destinationPhoneNumber,
    destinationAgentId,
    small,
  } = props;
  const setTransfered = useSetTransfered();

  const { iqSessionId }: any = usePuzzelExtendedStateInformation();

  const { userId } = useUserValidationState();

  const [transferedResponse, performTransfer] = useApiAction(
    sessionCommandObject,
    false,
  );

  const [, performSessionCommandObject] = useApiAction(
    sessionCommandObject,
    false,
  );

  useEffect(() => {
    if (transferedResponse.data?.code === 0) {
      setTransfered(true);
    } else if (transferedResponse.data !== null) {
      log(
        LogLevel.warn,
        "SessionCommand",
        `Unexpected response when transferring phone call: ${transferedResponse.data?.code} - ${transferedResponse.data?.message}.`,
      );
    }
  }, [transferedResponse.data?.code]);

  const param = {
    userId,
    iqSessionId,
  };
  const payload = {
    currentUserId: userId,
    requestId,
  };

  function transferTo() {
    performTransfer({
      payload: {
        ...payload,
        command,
        phoneNumber: destinationPhoneNumber,
        agentId: destinationAgentId,
      },
      param,
    });
  }

  function consultWith() {
    performSessionCommandObject({
      payload: {
        ...payload,
        command,
        phoneNumber: destinationPhoneNumber,
        agentId: destinationAgentId,
      },
      param,
    });
  }

  function sessionCommand() {
    performSessionCommandObject({
      payload: { ...payload, command },
      param,
    });
  }

  const { disabled } = props;
  let onSessionClick = sessionCommand;

  switch (command) {
    case SessionCommands.ConsultWithAgent:
    case SessionCommands.ConsultWithPhone:
      onSessionClick = consultWith;
      break;
    case SessionCommands.TransferToAgent:
    case SessionCommands.TransferToPhone:
      onSessionClick = transferTo;
      break;

    case SessionCommands.ClientConnected:
    case SessionCommands.ConferenceAdd:
    case SessionCommands.ConferenceRemove:
    case SessionCommands.ConsultOff:
    case SessionCommands.Hangup:
    case SessionCommands.MuteOff:
    case SessionCommands.MuteOn:
    case SessionCommands.TransferToConsultant:
    case SessionCommands.TransferToPersonalQueue:
    case SessionCommands.TransferToQueue:
    case SessionCommands.TransferToService:
  }

  return (
    <DropsButton
      leftIcon={getButtonIconText(command, small).icon}
      variant="tertiary"
      size="sm"
      disabled={disabled}
      onClick={onSessionClick}
    >
      {getButtonIconText(command, small).text}
    </DropsButton>
  );
};

SessionCommand.defaultProps = {
  destinationPhoneNumber: undefined,
  disabled: undefined,
  destinationAgentId: undefined,
};

export default SessionCommand;
